import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { ArrowLeft } from 'lucide-react';

import { ReactComponent as NoJobIcon } from '../../assets/images/no-job-icon.svg';
import Header from '../../components/shared/Header/Header';
import { Pagination } from '../../components/shared/Pagination/Pagination';
import ActivityFeedLoader from '../../components/shared/SkeletonLoaders/ActivityFeedLoader/ActivityFeedLoader';
import { getRecommendedJobs } from '../../redux/actions/dashboardActions';
import { selectRecommendedJobs, selectRecommendedJobsLoading } from '../../selectors/dashboardSelectors';
import { selectCurrentOrganization } from '../../selectors/organizationSelectors';
import JobSection from '../Browse/OpeningsListElement/JobSection';

import styles from '../Browse/OpeningsListElement/OpeningsListElement.module.scss';

export const RecommendedJobsPage = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const LIMIT = 5;
  const [currentPage, setCurrentPage] = useState(1);

  const recommendedJobs = useSelector(selectRecommendedJobs);
  const recommendedJobsLoading = useSelector(selectRecommendedJobsLoading);
  const loading = useSelector((state) => state.globalReducer.loading);
  const currentOrganization = useSelector(selectCurrentOrganization);

  const jobRecommendations = recommendedJobs?.job_recommendations || [];
  const totalCount = recommendedJobs?.total_count || 0;
  const totalPages = Math.ceil(totalCount / LIMIT);

  // Fetch jobs with pagination (offset and limit)
  const fetchRecommendedJobs = (page) => {
    const offset = (page - 1) * LIMIT;
    const params = {
      limit: LIMIT,
      offset,
      ...(currentOrganization?.name === 'Boon' ? { boon_view: true } : { organization_id: currentOrganization?.id }),
    };

    dispatch(getRecommendedJobs(params));
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchRecommendedJobs(newPage);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (currentOrganization) {
      setCurrentPage(1);
      fetchRecommendedJobs(1);
    }
  }, [currentOrganization?.id, currentOrganization?.name]);

  const renderJobsLoader = () => {
    if (recommendedJobsLoading || loading) {
      return <ActivityFeedLoader itemCount={6} fromRecommendationsPage />;
    }
    return null;
  };

  const renderJobsEmptyState = () => {
    if (!recommendedJobsLoading && !loading && totalCount === 0 && jobRecommendations.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center p-4 text-center md:p-8">
          <NoJobIcon />
          <p className="text-[#0E1829] font-medium text-base">{t('recommendations.noMatchingJobs')}</p>
        </div>
      );
    }
    return null;
  };

  const renderJobsContent = () => {
    if (recommendedJobsLoading || loading) {
      return renderJobsLoader();
    }

    if (jobRecommendations.length > 0) {
      return (
        <>
          <div className="flex flex-row items-center justify-between">
            <div className="flex flex-col">
              <p className="text-lg font-semibold text-gray-900">{t('recommendations.recommendedJobs')}</p>
              <p className="text-xs text-gray-600">{t('recommendations.basedOnProfile')}</p>
            </div>
          </div>
          <div className="space-y-4">
            {jobRecommendations.map((job, index) => (
              <div
                key={job.job_id ? `job-${job.job_id}` : `job-${index}`}
                className={classNames(styles.openingItemParent)}
                role="presentation"
              >
                <JobSection job={job.job_data} matchScore={job.match_score} />
              </div>
            ))}
          </div>
          <div className="flex justify-center">
            <Pagination totalPages={totalPages} currentPage={currentPage} setCurrentPage={handlePageChange} />
          </div>
        </>
      );
    }

    return renderJobsEmptyState();
  };

  return (
    <>
      <div className="max-md:hidden">
        <Header title={t('recommendations.recommendedJobs')} backButton={t('headers.backToDashboard')} />
      </div>
      <div className="h-[calc(100vh-84px)] overflow-y-scroll">
        <div className="flex items-center justify-between h-16 px-3 py-4 bg-white md:hidden">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 p-0 mt-1 text-xs font-medium text-gray-500 bg-transparent border-none"
          >
            <ArrowLeft />
          </button>
          <p className="text-xl font-semibold text-gray-900">{t('recommendations.recommendedJobs')}</p>
          <span />
        </div>
        <div className="flex items-center justify-center p-[26px] max-w-screen-lg mx-auto">
          <div className="flex flex-col w-full gap-3 p-3 bg-white rounded-xl shadow-card lg:w-3/4">
            {renderJobsContent()}
          </div>
        </div>
      </div>
    </>
  );
};
